import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { Badge } from "../components/ui/badge";
import { MapPin } from "lucide-react";
import { API } from "../App";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const CatalogBookDetailPage = () => {
  const { bookId } = useParams();
  const [book, setBook] = useState(null);
  const [copies, setCopies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await axios.get(`${API}/catalog/${bookId}`);
        setBook(data.book);
        setCopies(data.copies);
      } catch {
        setBook(null);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [bookId]);

  if (loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: "var(--background)" }}>
        <Navbar />
        <div className="text-center py-20 text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!book) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: "var(--background)" }}>
        <Navbar />
        <div className="text-center py-20 text-gray-500">Book not found.</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--background)" }}>
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 py-10" data-testid="catalog-book-detail">
        <div className="flex gap-6">
          {book.cover_url ? (
            <img
              src={book.cover_url}
              alt={book.title}
              className="h-64 w-44 object-cover rounded-lg bg-gray-100 flex-shrink-0"
            />
          ) : (
            <div className="h-64 w-44 rounded-lg bg-gray-200 flex items-center justify-center text-gray-400 flex-shrink-0">
              No cover
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold" style={{ color: "var(--primary)" }} data-testid="catalog-detail-title">
              {book.title}
            </h1>
            <p className="text-lg text-gray-700 mt-1" data-testid="catalog-detail-author">{book.author}</p>
            {book.isbn && (
              <p className="text-sm text-gray-500 mt-1">ISBN: {book.isbn}</p>
            )}
            {book.genre?.length > 0 && (
              <div className="flex gap-1 mt-3 flex-wrap">
                {book.genre.map((g) => (
                  <Badge key={g} variant="secondary" className="text-xs">{g}</Badge>
                ))}
              </div>
            )}
            {book.description && (
              <p className="text-sm text-gray-600 mt-4 leading-relaxed">{book.description}</p>
            )}
            {(() => {
              const pins = [...new Set((copies || []).map((c) => c.pincode).filter(Boolean))];
              if (!pins.length) return null;
              return (
                <div className="flex flex-wrap items-center gap-1.5 mt-3 text-sm text-gray-600">
                  <MapPin className="h-4 w-4" />
                  {pins.map((p) => (
                    <span key={p} className="bg-gray-100 px-2.5 py-0.5 rounded-full text-gray-800">{p}</span>
                  ))}
                </div>
              );
            })()}
            <div className="text-sm text-gray-500 mt-3">
              Source: <Badge variant="outline" className="text-xs capitalize">{book.source}</Badge>
            </div>
          </div>
        </div>

        <div className="mt-10">
          <h2 className="text-xl font-semibold mb-4">
            Available Copies ({copies.length})
          </h2>
          {copies.length === 0 ? (
            <div className="text-center py-8 text-gray-500 border border-dashed rounded-lg" data-testid="no-copies">
              No copies available for this book yet.
            </div>
          ) : (
            <div className="space-y-3" data-testid="copies-list">
              {copies.map((c) => (
                <div
                  key={c.id}
                  className="p-4 bg-white rounded-lg border border-gray-200 flex items-center justify-between"
                  data-testid={`copy-${c.id}`}
                >
                  <div>
                    <div className="font-medium">{c.owner_name}</div>
                    <div className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                      <MapPin className="h-3 w-3" /> {c.city} - {c.pincode}
                    </div>
                    {c.notes && (
                      <div className="text-xs text-gray-500 mt-1">{c.notes}</div>
                    )}
                  </div>
                  <div className="text-right">
                    <Badge variant="secondary" className="capitalize">{c.condition}</Badge>
                    <div className="text-sm text-gray-700 mt-1">
                      Rs.{c.daily_rate}/day
                    </div>
                    <div className="text-xs text-gray-500">
                      Deposit: Rs.{c.deposit}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CatalogBookDetailPage;
